import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../../../../firebase/AuthContext";
import { getApiUrl } from "../../../../../../config/api";
import VideoLibraryRow from "./VideoLibraryRow";

const PAGE_SIZE = 20;

function VideoLibrary({ language, addedIds = [] }) {
    const { currentUser } = useAuth();
    const [search, setSearch] = useState("");
    const [query, setQuery] = useState("");
    const [page, setPage] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const [library, setLibrary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const t = (en, zh) => (language === "zh" ? zh : en);

    useEffect(() => {
        let active = true;
        const controller = new AbortController();
        setLoading(true);
        setError(false);
        setLibrary(null);
        async function load() {
            try {
                const token = await currentUser.getIdToken();
                const { data } = await axios.get(`${getApiUrl()}/api/video/list`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { page, page_size: PAGE_SIZE, q: query },
                    timeout: 30_000,
                    signal: controller.signal,
                });
                if (data?.result !== "success" || data.source !== "video_agent" ||
                    !Array.isArray(data.videos) || !Number.isInteger(data.pagination?.total) ||
                    data.pagination.total < 0 || !Number.isInteger(data.pagination.total_pages) ||
                    data.pagination.page !== page || data.pagination.page_size !== PAGE_SIZE) {
                    throw new Error("Invalid video library response");
                }
                if (active) {
                    // A concurrent deletion can remove this page while it is loading.
                    if (data.videos.length === 0 && page > Math.max(1, data.pagination.total_pages)) {
                        setPage(Math.max(1, data.pagination.total_pages));
                    } else setLibrary(data);
                }
            } catch (err) {
                if (active) setError(true);
            } finally {
                if (active) setLoading(false);
            }
        }
        load();
        return () => {
            active = false;
            controller.abort();
        };
    }, [currentUser, page, query, refresh]);

    const handleSearch = (event) => {
        event.preventDefault();
        setQuery(search.trim());
        setPage(1);
        setRefresh((value) => value + 1);
    };

    const handleDelete = () => {
        // Use the current query/page, even if the user navigated during DELETE.
        // The fresh response above corrects pages removed by this or another user.
        setRefresh((value) => value + 1);
    };

    const formatDate = (value) => {
        const date = value ? new Date(value) : null;
        return date && !Number.isNaN(date.getTime())
            ? new Intl.DateTimeFormat(language === "zh" ? "zh-TW" : "en", {
                year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
            }).format(date)
            : "—";
    };

    return (
        <section aria-label={t("Indexed videos", "已收錄影片")}
            className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5 space-y-4 border-b border-gray-100">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{t("Indexed videos", "已收錄影片")}</h3>
                        {library && <p className="text-sm text-gray-500">
                            {library.pagination.total} {query ?
                                t(library.pagination.total === 1 ? "matching video" : "matching videos", "支符合條件的影片") :
                                t(library.pagination.total === 1 ? "video" : "videos", "支影片")}
                        </p>}
                    </div>
                    <button type="button" disabled={loading} onClick={() => setRefresh((value) => value + 1)}
                        className="text-sm font-semibold text-orange-600 disabled:opacity-40">
                        {t("Refresh", "重新整理")}
                    </button>
                </div>
                <form onSubmit={handleSearch} className="flex gap-3">
                    <input type="search" aria-label={t("Search indexed videos", "搜尋已收錄影片")}
                        value={search} maxLength={200} onChange={(event) => setSearch(event.target.value)}
                        placeholder={t("Search title or video ID", "搜尋標題或影片 ID")}
                        className="min-w-0 flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-400" />
                    <button type="submit" className="px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold">
                        {t("Search", "搜尋")}
                    </button>
                </form>
            </div>
            <div aria-live="polite">
                {loading && <p className="p-6 text-sm text-gray-500">{t("Loading videos…", "正在載入影片…")}</p>}
                {error && <p role="alert" className="p-6 text-sm text-red-600">
                    {t("Could not load videos. Select Refresh to try again.", "無法載入影片，請按重新整理再試一次。")}
                </p>}
                {library && library.videos.length === 0 && <p className="p-6 text-sm text-gray-500">
                    {library.pagination.total > 0 ? t("No videos on this page. Select Previous.", "這一頁沒有影片，請回到上一頁。") :
                        query ? t("No videos match your search.", "沒有符合搜尋條件的影片。") :
                        t("No videos have been indexed yet.", "目前尚未收錄影片。")}
                </p>}
            </div>
            {library && library.videos.length > 0 && <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-500">
                        <tr>
                            <th className="px-5 py-3 font-medium">{t("Video", "影片")}</th>
                            <th className="px-3 py-3 font-medium">{t("Language", "字幕語言")}</th>
                            <th className="px-3 py-3 font-medium">{t("Status", "狀態")}</th>
                            <th className="px-5 py-3 font-medium">{t("Indexed", "收錄時間")}</th>
                            <th className="px-5 py-3 font-medium">{t("Actions", "操作")}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {library.videos.map((video) => <VideoLibraryRow key={video.video_id}
                            video={video} language={language} added={addedIds.includes(video.video_id)} formatDate={formatDate}
                            onDelete={handleDelete}
                            onStatusChange={(updated) => setLibrary((current) => current && ({ ...current,
                                videos: current.videos.map((row) => row.video_id === updated.video_id ?
                                    { ...row, status: updated.status, chunk_count: updated.chunk_count } : row),
                            }))} />)}
                    </tbody>
                </table>
            </div>}
            {library && (library.pagination.total_pages > 1 || page > 1) &&
                <nav aria-label={t("Video pages", "影片分頁")} className="flex items-center justify-between p-5 border-t border-gray-100 text-sm">
                    <button type="button" disabled={loading || page <= 1} onClick={() => setPage((value) => value - 1)}
                        className="text-orange-600 disabled:opacity-40">{t("Previous", "上一頁")}</button>
                    <span className="text-gray-500">{t("Page", "第")} {page} / {Math.max(1, library.pagination.total_pages)}</span>
                    <button type="button" disabled={loading || page >= library.pagination.total_pages} onClick={() => setPage((value) => value + 1)}
                        className="text-orange-600 disabled:opacity-40">{t("Next", "下一頁")}</button>
                </nav>}
        </section>
    );
}

export default VideoLibrary;
